import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ISSUE_LABEL, type IssueReason, type NamespaceSummary } from "@k8s-dashboard/contracts";
import { useNamespaceList, drillKeys } from "@/api/queries";
import { Panel, StatusBadge } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { IssueFilter, UsageBar, withSearch } from "@/components/drill";
import { num } from "@/lib/format";
import { useDashboardParams } from "@/state/useDashboardParams";
import { PageError, PageHeader, useDrillControls, useInvalidate, useIssueFilter } from "./common";
import { useDashboardStore } from "@/state/dashboardStore";

/** Namespace 목록 — Cluster Overview 다음 단계. 여기서 문제 Namespace를 고릅니다. */
export function NamespaceList() {
  const { search } = useLocation();
  const { clusterId, range, from, to, refreshMs } = useDashboardParams();
  const namespaceSelection = useDashboardStore((state) => state.namespaceSelection);
  const q = useNamespaceList(clusterId, range, refreshMs, from, to);
  const invalidate = useInvalidate(drillKeys.namespaces(clusterId, range));
  /* 이 화면은 Namespace 자체가 주제라 전역 선택으로 목록을 **걸러내지 않습니다** — 하나만
     남으면 고를 자리가 사라집니다. 대신 지금 고른 것을 맨 위에 선택 상태로 보입니다.
     선택을 "all"로 되돌리지도 않습니다 — 다른 화면으로 돌아갔을 때 사용자의 선택이
     남아야 합니다. (#94 · #111) */
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);

  /* 이름 검색은 사용자 상태이므로 URL(q)에 둡니다 — 자동 갱신이 지우지 않습니다. */
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const setQuery = (v: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (v) p.set("q", v);
        else p.delete("q");
        return p;
      },
      { replace: true },
    );

  const filter = useIssueFilter();

  const list = q.data?.namespaces.data ?? [];
  const available = [...new Set(list.flatMap((n) => n.issues))] as IssueReason[];
  const counts = Object.fromEntries(
    available.map((r) => [r, list.filter((n) => n.issues.includes(r)).length]),
  ) as Partial<Record<IssueReason, number>>;

  const needle = query.trim().toLowerCase();
  const matched = list.filter(
    (n) =>
      (!needle || n.name.toLowerCase().includes(needle)) &&
      (!filter.selected.length || filter.selected.every((r) => n.issues.includes(r))),
  );
  /* 고른 Namespace를 맨 위로 올립니다. 검색·필터에서 빠졌다면 올리지 않습니다 —
     사용자가 방금 좁힌 결과에 없는 행을 끼워 넣지 않습니다. */
  const selectedNames = namespaceSelection?.mode === "selected" ? namespaceSelection.names : [];
  const picked = selectedNames.flatMap((name) => matched.filter((item) => item.name === name));
  const pickedSet = new Set(picked.map((item) => item.name));
  const shown = [...picked, ...matched.filter((item) => !pickedSet.has(item.name))];

  return (
    <div className="page">
      <PageHeader
        title="Namespaces"
        subtitle={`${clusterId} · 접근 가능한 Namespace만 표시됩니다`}
        controls={controls}
        actions={
          <>
            <label className="field">
              <span className="visually-hidden">Namespace 이름 검색</span>
              <input
                type="search"
                value={query}
                placeholder="Namespace 이름 검색"
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
            <IssueFilter
              available={available}
              selected={filter.selected}
              counts={counts}
              onToggle={filter.toggle}
              onClear={filter.clear}
            />
          </>
        }
      />

      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <Panel
          title="Namespace 상태"
          subtitle={`${shown.length}/${list.length}개 표시`}
          section={q.data?.namespaces}
          referenceIso={q.data?.generatedAt}
          flush
        >
          <SectionView
            section={q.data?.namespaces}
            loading={q.isLoading}
            emptyTitle="Namespace가 없습니다"
            emptyDetail="이 클러스터에서 접근 가능한 Namespace가 없습니다."
          >
            {() => <NamespaceTable items={shown} search={search} selected={selectedNames} />}
          </SectionView>
        </Panel>
      )}
    </div>
  );
}

function NamespaceTable({
  items,
  search,
  selected,
}: {
  items: NamespaceSummary[];
  search: string;
  /** Header에서 고른 전역 Namespace. 목록에 없으면 undefined입니다. */
  selected: readonly string[];
}) {
  /* 상세 링크에 이 화면 전용 파라미터(이름 검색어)를 넘기지 않습니다. (#2) */
  const cleaned = new URLSearchParams(search);
  cleaned.delete("q");
  const cleanedSearch = cleaned.toString();

  if (items.length === 0) {
    return (
      <div className="state">
        <span className="state__glyph" aria-hidden="true">
          ✓
        </span>
        <span className="state__title">필터에 맞는 Namespace가 없습니다</span>
        <span className="state__detail">이름 검색어 또는 선택한 상태 조건을 만족하는 Namespace가 없습니다.</span>
      </div>
    );
  }
  return (
    <div className="panel__scroll" style={{ maxHeight: 620 }}>
      <table className="ds-data-table">
        <thead>
          <tr>
            <th>Namespace</th>
            <th style={{ width: 120 }}>상태</th>
            <th className="ds-num" style={{ width: 110 }}>
              Workload
            </th>
            <th className="ds-num" style={{ width: 110 }}>
              Pod
            </th>
            <th className="ds-num" style={{ width: 90 }}>
              재시작
            </th>
            <th style={{ width: 190 }}>CPU / Request</th>
            <th style={{ width: 190 }}>Memory / Request</th>
            <th>문제</th>
          </tr>
        </thead>
        <tbody>
          {items.map((n) => (
            /* 색만으로 알리지 않습니다 — 선택 표시를 글자로도 답니다. */
            <tr key={n.name} className={selected.includes(n.name) ? "is-selected" : undefined}>
              <td className="ds-ident">
                <Link to={withSearch(`/namespaces/${encodeURIComponent(n.name)}`, cleanedSearch)}>{n.name}</Link>
                {selected.includes(n.name) && <span className="badge--selected">선택됨</span>}
              </td>
              <td>
                <StatusBadge severity={n.severity} small />
              </td>
              <td className="ds-num">
                {num(n.workloads.total - n.workloads.unhealthy)}/{num(n.workloads.total)}
              </td>
              <td className="ds-num">
                {num(n.pods.running)}/{num(n.pods.total)}
              </td>
              <td className="ds-num">{num(n.pods.restarts)}</td>
              <td>
                <UsageBar ratio={n.usage.cpuVsRequest} label="CPU Request" />
              </td>
              <td>
                <UsageBar ratio={n.usage.memoryVsRequest} label="Memory Request" />
              </td>
              <td className="muted">
                {n.issues.length ? n.issues.map((i) => ISSUE_LABEL[i]).join(" · ") : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
